import type { components } from "@/src/api/api";

/**
 * Charge utile de `GET /api/v1/moderation/reports`.
 *
 * ⚠️ `data` est **facultatif** dans le contrat — le `NonNullable` est ce qui
 * permet d'en dériver le reste sans que tout devienne `| undefined`.
 */
type ReportsPayload = NonNullable<
  components["schemas"]["GetReportsApiResponseSchema"]["data"]
>;

/**
 * Un signalement, tel que le serveur le renvoie.
 *
 * Dérivé du contrat, jamais recopié à la main : une colonne ajoutée ou renommée
 * côté serveur devient une erreur de `tsc` ici après `bun run api:generate`.
 */
export type Report = ReportsPayload["reports"][number];

/** Charge utile de `GET /moderation/reports/{id}/context`. */
type ReportContextPayload = NonNullable<
  components["schemas"]["GetReportContextApiResponseSchema"]["data"]
>;

export type ReportContextMessage = ReportContextPayload["messages"][number];

/**
 * Une réaction, comptes **résolus**.
 *
 * ⚠️ Ce n'est pas la forme de `/conversation/message/{id}/reaction`, qui
 * n'expose que des `accountIds` : celle-là est diffusée à tous les
 * participants dans un JSON identique pour chacun. Ici il n'y a ni diffusion
 * ni destinataire multiple, et la modération a besoin de noms, pas de numéros.
 */
export type MessageReaction = ReportContextMessage["reactions"][number];

/**
 * Un compte tel que la modération le voit : de quoi le nommer et le montrer,
 * rien de plus — ni e-mail, ni rôles, ni localisation.
 *
 * ⚠️ **Toujours nullable là où il apparaît.** Le serveur renvoie `null` pour le
 * compte supprimé entre-temps, pour le verdict pas encore rendu, et pour la
 * cible qui n'est pas un compte. Les trois se disent de la même façon à
 * l'écran, ce qui est exactement la raison pour laquelle ils ont la même forme.
 */
export type AccountIdentity = NonNullable<Report["reporter"]>;

export type ReportStatus = Report["status"];
export type ReportReason = Report["reason"];
export type ReportTarget = Report["targetType"];

/**
 * Les deux issues d'une clôture.
 *
 * `pending` n'en est pas une : on ne « repousse » pas un signalement dans la
 * file, et `PATCH /moderation/reports/{id}` ne l'accepte pas non plus.
 */
export type ReportOutcome = Exclude<ReportStatus, "pending">;

/** L'ordre des filtres à l'écran — la file en attente d'abord. */
export const REPORT_STATUSES = [
  "pending",
  "actioned",
  "dismissed",
] as const satisfies readonly ReportStatus[];

/**
 * Reconnaît un statut.
 *
 * Sert au retour de `ToggleButtonGroup`, qui parle en `Key` (`string | number`)
 * et ne sait rien de notre énumération.
 */
export function isReportStatus(value: unknown): value is ReportStatus {
  return REPORT_STATUSES.some((status) => status === value);
}
